import { __awaiter } from 'tslib';
import * as Long from 'long';
import { im_proto } from '../proto/index.js';
import { ErrorType } from '../core/consts.js';
import { Logger } from '../core/metrics/logger.js';
import { BaseError } from '../core/errors/BaseError.js';
import '../core/metrics/monitor.js';
import '../core/network/NetworkItem.js';
import { ContextClassBase, ServiceIdentity } from '../core/service/RuntimeContext.js';
import 'json-long';
import 'moize';
import '../core/model/Message.js';
import 'js-md5';
import '../core/utils/lz4.js';
import '../core/utils/serializer.js';
import { AdapterManager } from '../adapter/manager/AdapterManager.js';
import { AdapterType } from '../adapter/manager/types.js';
import { Ticker } from '../core/utils/ticker.js';
import '../adapter/abstract/IMNetApi.js';
import '../core/options.js';
import '../core/utils/uuid.js';
import '../core/service/ConversationManager.js';
import '../core/service/MessageManager.js';
import '@byted/uc-secure-sdk';
import '../core/plugin/DbProxy.js';
import { TrackerItem } from '../core/metrics/trackerTypes.js';
import '../core/model/CommandMessage.js';
import { IMRtcEvent } from '../adapter/abstract/IMRtcApi.js';
import { RtcChatInfo } from './model/RtcChatInfo.js';
import { RtcParticipant } from './model/RtcParticipant.js';
import { RtcEvent } from './RtcEvent.js';

class RtcManager extends ContextClassBase {
    constructor(ctx) {
        super(ctx);
        this.initSuccess = false;
        this.info = null;
        this.client = null;
        this.rtcSupport = false;
        // stream
        this.stream = null;
        this.dispatchEvent = (e) => {
            this.emit(e.type, e);
        };
        this.engineHandleOnline = (params) => {
            this.checkInfo();
            Logger.ctxDebug(this.ctx, `register rtc user: ${params.uid}`);
            let participant = this.info.participant.get(params.uid); // 如果不存在, 要先添加
            if (!participant) {
                Logger.ctxDebug(this.ctx, 'fallback add participant:', params.uid);
                participant = new RtcParticipant(params.uid);
                this.info.participant.set(params.uid, participant);
            }
            participant.status = im_proto.VoipStatus.ACCEPTED;
        };
        this.engineHandleLeave = (params) => __awaiter(this, void 0, void 0, function* () {
            this.checkInfo();
            if (!this.info.participant.has(params.uid)) {
                Logger.ctxWarn(this.ctx, `local participant not exist: ${params.uid}`);
                return;
            }
            const participant = this.info.participant.get(params.uid);
            if (participant.stream) {
                participant.stream.stop();
                participant.stream.close();
            }
            this.info.participant.delete(params.uid);
            if (this.info.participant.size === 1) { // 只有一个人时结束
                Logger.ctxDebug(this.ctx, 'terminate call since member 1:', this.info);
                this.emit(RtcEvent.Terminated, this.info);
                yield this.terminate(false);
            }
        });
        this.engineHandleStreamAdded = (params) => __awaiter(this, void 0, void 0, function* () {
            this.checkInfo();
            this.checkClient();
            // 收到新stream的时候立即subscribe
            try {
                yield this.client.subscribe(params.stream);
            }
            catch (err) {
                throw new BaseError({
                    ctx: this.ctx,
                    sender: this.client,
                    innerError: err,
                    type: ErrorType.RtcSdkError,
                    msg: 'subscribe stream error'
                });
            }
        });
        this.engineHandleStreamSubscribed = (params) => {
            var _a, _b;
            if (!this.checkEngineHandlerParam(params)) {
                return;
            }
            Logger.ctxDebug(this.ctx, `add stream for uid: ${(_a = params.stream.uid) !== null && _a !== void 0 ? _a : params.uid}`);
            const participant = this.info.participant.get((_b = params.stream.uid) !== null && _b !== void 0 ? _b : params.uid);
            participant.stream = params.stream;
            participant.status = im_proto.VoipStatus.ONTHECALL;
        };
        this.engineHandleMuteAudio = (params) => {
            var _a;
            if (!this.checkEngineHandlerParam(params)) {
                return;
            }
            this.info.participant.get((_a = params.stream.uid) !== null && _a !== void 0 ? _a : params.uid).audio = false;
        };
        this.engineHandleUnmuteAudio = (params) => {
            var _a;
            if (!this.checkEngineHandlerParam(params)) {
                return;
            }
            this.info.participant.get((_a = params.stream.uid) !== null && _a !== void 0 ? _a : params.uid).audio = true;
        };
        this.engineHandleMuteVideo = (params) => {
            var _a;
            if (!this.checkEngineHandlerParam(params)) {
                return;
            }
            this.info.participant.get((_a = params.stream.uid) !== null && _a !== void 0 ? _a : params.uid).video = false;
        };
        this.engineHandleUnmuteVideo = (params) => {
            var _a;
            if (!this.checkEngineHandlerParam(params)) {
                return;
            }
            this.info.participant.get((_a = params.stream.uid) !== null && _a !== void 0 ? _a : params.uid).video = true;
        };
        this.rtcApi = new (AdapterManager.getConstructor(ctx, AdapterType.RtcApi))(this.ctx);
    }
    checkSystemRequirements() {
        if (this.rtcSupport) {
            return true;
        }
        this.rtcSupport = this.rtcApi.checkSystemRequirements();
        return this.rtcSupport;
    }
    init(apiManager) {
        return __awaiter(this, void 0, void 0, function* () {
            this.api = apiManager;
            if (!this.checkSystemRequirements()) {
                return false;
            }
            if (!this.ctx.option.debug) { // 垃圾日志太多, 看情况要不要改
                this.rtcApi.setLogLevel('NONE');
            }
            this.initSuccess = true;
            return true;
        });
    }
    initClient() {
        return __awaiter(this, void 0, void 0, function* () {
            this.client = this.rtcApi.createClient(this.ctx.option.rtcAppId);
            try {
                yield this.client.init();
            }
            catch (err) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcSdkError,
                    innerError: err,
                    msg: 'init client error',
                    sender: this.client
                });
            }
        });
    }
    reset(apiManager) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.initSuccess) {
                yield this.init(apiManager);
            }
            const oldInfo = (_a = this.info) === null || _a === void 0 ? void 0 : _a.clone();
            // this.event = null;
            this.stopCallingTimer();
            this.stopOnTheCallTimer();
            this.info = null;
            try {
                if (this.stream) {
                    yield this.unpublish();
                    this.releaseStream();
                }
                if (this.client) {
                    this.unsubscribeRawEvent();
                    yield this.leaveChannel();
                }
                const oldClient = this.client;
                this.client = null;
                yield this.initClient();
                this.subscribeRawEvent();
                // 最后再 leave, 确保前面的执行完了
                yield (oldClient === null || oldClient === void 0 ? void 0 : oldClient.leave());
            }
            catch (err) {
                Logger.ctxError(this.ctx, 'reset async process err', err);
            }
            // 强制重置状态
            if (oldInfo) {
                void this.api.UpdateVoip({
                    channelId: oldInfo.channelId,
                    status: im_proto.VoipStatus.TERMINATED,
                    type: oldInfo.voipType
                }).then(result => {
                    if ((result === null || result === void 0 ? void 0 : result.status_code) !== im_proto.VoipStatusCode.SUCCESS) {
                        Logger.ctxDebug(this.ctx, `reset status error: ${im_proto.VoipStatusCode[result.status_code]}`);
                    }
                }).catch(err => {
                    Logger.ctxDebug(this.ctx, 'reset status error: ', err);
                });
            }
            this.emit(RtcEvent.ManagerReset, this);
            return oldInfo;
        });
    }
    // param check
    checkInit() {
        if (!this.initSuccess) {
            throw new BaseError({
                ctx: this.ctx,
                type: ErrorType.RtcNotInit,
                sender: this,
                msg: 'not init'
            });
        }
    }
    checkClient() {
        this.checkInit();
        if (!this.client) {
            throw new BaseError({
                ctx: this.ctx,
                type: ErrorType.RtcNoClient,
                sender: this,
                msg: 'no client provided'
            });
        }
    }
    checkStream() {
        this.checkClient();
        if (!this.stream) {
            throw new BaseError({
                ctx: this.ctx,
                type: ErrorType.RtcNoStream,
                sender: this,
                msg: 'no stream provided'
            });
        }
    }
    checkInfo() {
        this.checkInit();
        if (!this.info) {
            throw new BaseError({
                ctx: this.ctx,
                type: ErrorType.RtcNoInfo,
                sender: this,
                msg: 'no call info provided'
            });
        }
    }
    checkEngineHandlerParam(params) {
        var _a, _b;
        this.checkInfo();
        if (!this.info.participant.has((_a = params.stream.uid) !== null && _a !== void 0 ? _a : params.uid)) {
            Logger.ctxWarn(this.ctx, `rtc user: ${(_b = params.stream.uid) !== null && _b !== void 0 ? _b : params.uid} not exist in local participant`);
            return false;
        }
        return true;
    }
    // device
    getDevices() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.rtcApi.getDevices();
        });
    }
    createStream(audio = 'default', video = 'default') {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.stream !== null) {
                this.stream.close();
            }
            let audioEnabled = false;
            if (typeof audio === 'boolean' && audio || typeof audio === 'string') {
                audioEnabled = true;
            }
            let videoEnabled = false;
            if (typeof video === 'boolean' && video || typeof video === 'string') {
                videoEnabled = true;
            }
            this.audioEnabled = audioEnabled;
            this.videoEnabled = videoEnabled;
            Logger.ctxDebug(this.ctx, `start call with audio:${audioEnabled} && video:${videoEnabled}`);
            this.stream = this.rtcApi.createStream({
                audio: audioEnabled,
                video: videoEnabled
                // microphoneId: audioDevice,
                // cameraId: videoDevice
            });
            try {
                yield this.stream.init();
                return this.stream;
            }
            catch (err) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcSdkError,
                    msg: 'init stream error',
                    innerError: err,
                    sender: this.stream
                });
            }
        });
    }
    toggleVideo(enable) {
        var _a, _b, _c;
        if (!((_a = this.stream) === null || _a === void 0 ? void 0 : _a.hasVideo())) {
            this.videoEnabled = false;
            return false;
        }
        if (enable !== undefined) {
            this.videoEnabled = enable;
        }
        if (this.videoEnabled) {
            (_b = this.stream) === null || _b === void 0 ? void 0 : _b.muteVideo();
            this.videoEnabled = false;
        }
        else {
            (_c = this.stream) === null || _c === void 0 ? void 0 : _c.unmuteVideo();
            this.videoEnabled = true;
        }
        return this.videoEnabled;
    }
    toggleAudio(enable) {
        var _a, _b, _c;
        if (!((_a = this.stream) === null || _a === void 0 ? void 0 : _a.hasAudio())) {
            this.audioEnabled = false;
            return false;
        }
        if (enable !== undefined) {
            this.audioEnabled = enable;
        }
        if (this.audioEnabled) {
            (_b = this.stream) === null || _b === void 0 ? void 0 : _b.muteAudio();
            this.audioEnabled = false;
        }
        else {
            (_c = this.stream) === null || _c === void 0 ? void 0 : _c.unmuteAudio();
            this.audioEnabled = true;
        }
        return this.audioEnabled;
    }
    switchCamera() {
        return this.stream.switchCamera();
    }
    publish(videoBitrate, videoProfile, encoderConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.stream || !this.client) { // soft处理
                return;
            }
            // 不能subscribe 本地stream, 这里直接invoke下回调
            this.engineHandleStreamSubscribed({
                stream: this.stream,
                uid: this.ctx.option.userId
            });
            if (videoBitrate !== undefined) {
                this.stream.setVideoBitRate(videoBitrate);
            }
            if (videoProfile !== undefined) {
                this.stream.setVideoProfile(videoProfile);
            }
            if (encoderConfig !== undefined) {
                this.stream.setVideoEncoderConfiguration(encoderConfig);
            }
            try {
                yield this.client.publish(this.stream, {});
            }
            catch (err) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcSdkError,
                    msg: 'publish stream err',
                    sender: this.client,
                    innerError: err
                });
            }
        });
    }
    unpublish() {
        return __awaiter(this, void 0, void 0, function* () {
            this.checkStream();
            try { // 可能会失败, 包一层
                yield this.client.unpublish(this.stream);
            }
            catch (err) {
                Logger.ctxDebug(this.ctx, 'unpublish fail', err);
            }
        });
    }
    releaseStream() {
        if (!this.stream) {
            Logger.ctxDebug(this.ctx, 'no stream to release');
            return;
        }
        this.stream.stop();
        this.stream.close();
        this.stream = null;
        if (!this.info) {
            return;
        }
        for (const participant of this.info.participant.values()) {
            if (participant !== null && participant.stream) {
                participant.stream.stop();
                participant.stream.close();
            }
        }
    }
    // event
    setEvent(eventBus) {
        this.event = eventBus;
    }
    emit(event, e) {
        if (!this.event) {
            Logger.ctxDebug(this.ctx, `no event bus, ignore event: ${event}`);
            return;
        }
        // 保存当前的 eventbus 实例, 不然调度之前可能被reset干掉
        const eventbus = this.event;
        setTimeout(() => {
            Logger.ctxDebug(this.ctx, `emit rtc event to user: ${event}, args:`, e);
            eventbus.emit(event, this, e);
        }, 10);
    }
    enumKeys(obj) {
        return Object.keys(obj).filter(k => Number.isNaN(+k));
    }
    subscribeRawEvent() {
        this.checkClient();
        this.registerEngineEvent();
        const keys = this.enumKeys(IMRtcEvent);
        for (const key of keys) {
            this.client.on(IMRtcEvent[key], this.dispatchEvent);
        }
    }
    unsubscribeRawEvent() {
        this.checkClient();
        const keys = this.enumKeys(IMRtcEvent);
        for (const key of keys) {
            this.client.off(IMRtcEvent[key], this.dispatchEvent);
        }
        this.removeEngineEvent();
    }
    // 添加sdk需要的event callback
    registerEngineEvent() {
        this.checkClient();
        this.client.on(IMRtcEvent.PeerOnline, this.engineHandleOnline);
        this.client.on(IMRtcEvent.PeerLeave, this.engineHandleLeave);
        this.client.on(IMRtcEvent.StreamAdded, this.engineHandleStreamAdded);
        this.client.on(IMRtcEvent.StreamSubscribed, this.engineHandleStreamSubscribed);
        this.client.on(IMRtcEvent.MuteAudio, this.engineHandleMuteAudio);
        this.client.on(IMRtcEvent.UnmuteAudio, this.engineHandleUnmuteAudio);
        this.client.on(IMRtcEvent.MuteVideo, this.engineHandleMuteVideo);
        this.client.on(IMRtcEvent.UnmuteVideo, this.engineHandleUnmuteVideo);
    }
    removeEngineEvent() {
        this.checkClient();
        this.client.off(IMRtcEvent.PeerOnline, this.engineHandleOnline);
        this.client.off(IMRtcEvent.PeerLeave, this.engineHandleLeave);
        this.client.off(IMRtcEvent.StreamAdded, this.engineHandleStreamAdded);
        this.client.off(IMRtcEvent.StreamSubscribed, this.engineHandleStreamSubscribed);
        this.client.off(IMRtcEvent.MuteAudio, this.engineHandleMuteAudio);
        this.client.off(IMRtcEvent.UnmuteAudio, this.engineHandleUnmuteAudio);
        this.client.off(IMRtcEvent.MuteVideo, this.engineHandleMuteVideo);
        this.client.off(IMRtcEvent.UnmuteVideo, this.engineHandleUnmuteVideo);
    }
    // api
    // 开始一个和 userid 的对话
    startCall(type, idempotentId, toUsers) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            this.checkInit();
            const status = this.info === null ? null : this.info.voipStatus;
            // 正在通话的时候不能开始新的
            if (status === im_proto.VoipStatus.CALLING) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcAlreadyCalling,
                    sender: this,
                    msg: `already calling while start, current is ${im_proto.VoipStatus[(_b = (_a = this.info) === null || _a === void 0 ? void 0 : _a.voipStatus) !== null && _b !== void 0 ? _b : 0]}`
                });
            }
            if (status === im_proto.VoipStatus.ONTHECALL) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcAlreadyOnTheCall,
                    sender: this,
                    msg: `already on the call while start, current is ${im_proto.VoipStatus[(_d = (_c = this.info) === null || _c === void 0 ? void 0 : _c.voipStatus) !== null && _d !== void 0 ? _d : 0]}`
                });
            }
            if (typeof toUsers === 'string') {
                toUsers = [toUsers];
            }
            return this.joinChannelAndCall(type, idempotentId, toUsers);
        });
    }
    acceptCall(type) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            this.checkInfo();
            if (this.info.voipStatus !== im_proto.VoipStatus.RINGING) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcNotRinging,
                    sender: this,
                    msg: `not ringing while accept, current is ${im_proto.VoipStatus[(_b = (_a = this.info) === null || _a === void 0 ? void 0 : _a.voipStatus) !== null && _b !== void 0 ? _b : 0]}`
                });
            }
            const voipType = type !== null && type !== void 0 ? type : this.info.voipType;
            const result = yield this.api.UpdateVoip({
                channelId: this.info.channelId,
                status: im_proto.VoipStatus.ACCEPTED,
                type: voipType
            });
            this.resolve(ServiceIdentity.Monitor).emitTracker(TrackerItem.RtcAcceptCall, {
                imsdk_result: (result === null || result === void 0 ? void 0 : result.status_code) === im_proto.VoipStatusCode.SUCCESS ? '1' : '0',
                logid: (_c = result === null || result === void 0 ? void 0 : result.info) === null || _c === void 0 ? void 0 : _c.channel_id
            });
            if ((result === null || result === void 0 ? void 0 : result.status_code) === im_proto.VoipStatusCode.SUCCESS) {
                this.info.voipStatus = result.info.status;
                this.info.token = result.info.token;
                const typeChanged = this.checkChangeChatType(this.info.userId, result.info.v_type);
                yield this.acceptedToOnTheCall();
                yield this.joinChannel(this.info.token, this.info.channelId, this.info.userId);
                this.emit(RtcEvent.SelfAccept, {
                    info: this.info,
                    typeChanged
                });
            }
            else {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcAcceptFail,
                    sender: this,
                    msg: `accept voip error: ${im_proto.VoipStatusCode[result.status_code]}`
                });
            }
        });
    }
    cancelCall() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            this.checkInfo();
            // 通话中取消, 只停计时器
            if (this.info.voipStatus === im_proto.VoipStatus.ONTHECALL) {
                this.stopCallingTimer();
            }
            else if (this.info.voipStatus === im_proto.VoipStatus.CALLING) {
                // 起始呼叫取消则直接退出
                yield this.updateStatus(im_proto.VoipStatus.CANCELLED);
                this.stopCallingTimer();
                this.emit(RtcEvent.CancelCall, this.info);
                yield this.reset(this.api);
            }
            else {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcNotCalling,
                    sender: this,
                    msg: `not calling while cancel, current is ${im_proto.VoipStatus[(_b = (_a = this.info) === null || _a === void 0 ? void 0 : _a.voipStatus) !== null && _b !== void 0 ? _b : 0]}`
                });
            }
        });
    }
    denyCall() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            this.checkInfo();
            if (this.info.voipStatus !== im_proto.VoipStatus.RINGING) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcNotRinging,
                    sender: this,
                    msg: `not ringing while deny, current is ${im_proto.VoipStatus[(_b = (_a = this.info) === null || _a === void 0 ? void 0 : _a.voipStatus) !== null && _b !== void 0 ? _b : 0]}`
                });
            }
            yield this.updateStatus(im_proto.VoipStatus.REFUSED);
            this.emit(RtcEvent.SelfDeny, this.info);
            this.info.voipStatus = im_proto.VoipStatus.REFUSED;
            yield this.reset(this.api);
        });
    }
    terminate(byself) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            this.checkInfo();
            if (this.info.voipStatus !== im_proto.VoipStatus.ONTHECALL) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcNotOnTheCall,
                    sender: this,
                    msg: `not on the call while terminate, current is ${im_proto.VoipStatus[(_b = (_a = this.info) === null || _a === void 0 ? void 0 : _a.voipStatus) !== null && _b !== void 0 ? _b : 0]}`
                });
            }
            yield this.leaveChannel();
            yield this.updateStatus(im_proto.VoipStatus.TERMINATED);
            this.stopOnTheCallTimer();
            this.releaseStream();
            if (byself) {
                this.emit(RtcEvent.SelfTeminate, this.info);
            }
            // 有bug, 暂时用这种办法, 重建一个client规避
            yield this.reset(this.api);
        });
    }
    invite(target) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            this.checkInfo();
            if (typeof target === 'string') {
                target = [target];
            }
            const idempotentId = target.join(':');
            // 拉人时先创建新通话
            // 使用父channel id作为 ref channel
            const respCreate = yield this.api.CreateVoip({
                type: this.info.voipType,
                voipMode: im_proto.VoipMode.VOIP_MODE_MULTIPLAYER,
                refChannelId: this.info.channelId,
                idempotentId
            });
            if ((respCreate === null || respCreate === void 0 ? void 0 : respCreate.status_code) !== im_proto.VoipStatusCode.SUCCESS) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcCreateRoomFail,
                    sender: this,
                    msg: `create voip room status code: ${respCreate === null || respCreate === void 0 ? void 0 : respCreate.status_code} check failed`
                });
            }
            Logger.ctxDebug(this.ctx, `create room with ${(_a = respCreate === null || respCreate === void 0 ? void 0 : respCreate.info) === null || _a === void 0 ? void 0 : _a.channel_id} success`);
            // 记录子channel 信息
            const refCreateInfo = RtcChatInfo.fromVoipInfo(respCreate.info);
            const currentUsers = [];
            (_b = this.info) === null || _b === void 0 ? void 0 : _b.participant.forEach(p => currentUsers.push(p.userId));
            const callParams = {
                channelId: refCreateInfo.channelId,
                calleeIds: target,
                type: refCreateInfo.voipType,
                refChannelUsers: currentUsers
            };
            const respCall = yield this.api.CallVoip(callParams);
            if (respCall.status !== im_proto.VoipStatusCode.SUCCESS) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcStartCallFail,
                    sender: this,
                    msg: `call voip status code: ${respCall === null || respCall === void 0 ? void 0 : respCall.status} check failed`
                });
            }
            const resultBody = {
                payload: refCreateInfo,
                checkCode: respCall.check_code,
                checkMsg: respCall.check_message,
                body: respCall,
                statusCode: respCall.status,
                statusMsg: respCall.extra_info
            };
            if ((respCall === null || respCall === void 0 ? void 0 : respCall.check_code) !== undefined && Long.fromValue(respCall.check_code).notEquals(Long.ZERO)) {
                return Object.assign({ success: false }, resultBody);
            }
            this.startCallingTimer(callParams);
            // 写入原始的 info
            for (const uid of target) {
                if (!this.info.participant.has(uid)) { // 添加成员, 默认设置成 calling
                    const p = new RtcParticipant(uid);
                    p.status = im_proto.VoipStatus.CALLING;
                    this.info.participant.set(uid, p);
                }
            }
            return Object.assign({ success: true }, resultBody);
        });
    }
    joinChannel(token, channelId, userId) {
        return __awaiter(this, void 0, void 0, function* () {
            this.checkClient();
            try {
                yield this.client.join(token, channelId, userId);
                Logger.ctxDebug(this.ctx, `join channel: ${channelId} as user: ${userId} with token:${token}`);
            }
            catch (err) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcSdkError,
                    msg: 'client join channel error',
                    sender: this.client,
                    innerError: err
                });
            }
        });
    }
    leaveChannel() {
        return __awaiter(this, void 0, void 0, function* () {
            this.stopOnTheCallTimer();
            this.checkClient();
            // FIXME: 这里有bug, leave之后无法再次join
            try {
                yield this.client.leave();
                Logger.ctxDebug(this.ctx, 'leave channel');
            }
            catch (err) {
                throw new BaseError({
                    ctx: this.ctx,
                    msg: 'leave channel error',
                    sender: this.client,
                    type: ErrorType.RtcSdkError,
                    innerError: err
                });
            }
        });
    }
    // server operation
    updateStatus(newStatus) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.checkInfo();
            Logger.ctxDebug(this.ctx, `rtc state from ${im_proto.VoipStatus[this.info.voipStatus]} change to ${im_proto.VoipStatus[newStatus]}`);
            this.info.voipStatus = newStatus;
            const result = yield this.api.UpdateVoip({
                channelId: this.info.channelId,
                status: this.info.voipStatus,
                type: this.info.voipType
            });
            const eventMap = {
                [im_proto.VoipStatus.REFUSED]: TrackerItem.RtcDenyCall,
                [im_proto.VoipStatus.TERMINATED]: TrackerItem.RtcEnd
            };
            // @ts-ignore
            if (eventMap[this.info.voipStatus]) {
                // @ts-ignore
                this.resolve(ServiceIdentity.Monitor).emitTracker(eventMap[this.info.voipStatus], {
                    imsdk_result: (result === null || result === void 0 ? void 0 : result.status_code) === im_proto.VoipStatusCode.SUCCESS ? '1' : '0',
                    logid: (_a = result === null || result === void 0 ? void 0 : result.info) === null || _a === void 0 ? void 0 : _a.channel_id
                });
            }
            if ((result === null || result === void 0 ? void 0 : result.status_code) !== 0) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcInvalidParam,
                    sender: this,
                    msg: `update voip status error: ${im_proto.VoipStatusCode[result.status_code]}`
                });
            }
        });
    }
    // sdk
    createVoip(type, idempotentId, conversationShortId, serverMessageId) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const respCreate = yield this.api.CreateVoip({
                type,
                idempotentId,
                shortId: conversationShortId,
                serverMessageId,
                voipMode: this.target.length > 1 ? im_proto.VoipMode.VOIP_MODE_MULTIPLAYER : im_proto.VoipMode.VOIP_MODE_DOUBLE
            });
            if ((respCreate === null || respCreate === void 0 ? void 0 : respCreate.status_code) !== im_proto.VoipStatusCode.SUCCESS) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcCreateRoomFail,
                    sender: this,
                    msg: `create voip room status code: ${respCreate === null || respCreate === void 0 ? void 0 : respCreate.status_code} check failed`
                });
            }
            Logger.ctxDebug(this.ctx, `create room with ${(_a = respCreate === null || respCreate === void 0 ? void 0 : respCreate.info) === null || _a === void 0 ? void 0 : _a.channel_id} success`);
            this.info = RtcChatInfo.fromVoipInfo(respCreate.info);
        });
    }
    joinChannelAndCall(type, idempotentId, toUsers) {
        return __awaiter(this, void 0, void 0, function* () {
            if (Array.isArray(toUsers) && idempotentId !== undefined) {
                return this.joinChannelAndCallToUser(type, toUsers, idempotentId);
            }
            else if (!Array.isArray(toUsers) && toUsers !== undefined) {
                const conversation = toUsers;
                if (conversation.type === im_proto.ConversationType.ONE_TO_ONE_CHAT) { // 单聊固定给对方
                    toUsers = [conversation.toParticipantUserId];
                }
                else { // 群聊需要读取群成员
                    if (toUsers === undefined && conversation.participants !== undefined && Array.isArray(conversation.participants)) { // 不传成员默认给群内所有人
                        toUsers = conversation.participants.filter(p => p.userId !== this.ctx.option.userId).map(p => p.userId);
                    }
                    else { // 群聊没有成员, 无法发起
                        throw new BaseError({
                            ctx: this.ctx,
                            type: ErrorType.RtcInvalidParam,
                            sender: this,
                            msg: 'call conversation but no participant'
                        });
                    }
                }
                return this.joinChannelAndCallToUser(type, toUsers, conversation.shortId);
            }
            else {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcInvalidParam,
                    sender: this,
                    msg: 'unknown conversation target'
                });
            }
        });
    }
    joinChannelAndCallToUser(type, toUsers, idempotentId) {
        return __awaiter(this, void 0, void 0, function* () {
            this.target = toUsers;
            yield this.createVoip(type, idempotentId);
            this.checkInfo();
            const callParams = {
                channelId: this.info.channelId,
                calleeIds: toUsers,
                type,
                refChannelUsers: []
            };
            const respCall = yield this.api.CallVoip(callParams);
            if (respCall.status !== im_proto.VoipStatusCode.SUCCESS) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.RtcStartCallFail,
                    sender: this,
                    msg: `call voip status code: ${respCall === null || respCall === void 0 ? void 0 : respCall.status} check failed`
                });
            }
            const resultBody = {
                payload: this.info,
                checkCode: respCall.check_code,
                checkMsg: respCall.check_message,
                body: respCall,
                statusCode: respCall.status,
                statusMsg: respCall.extra_info
            };
            if ((respCall === null || respCall === void 0 ? void 0 : respCall.check_code) !== undefined && Long.fromValue(respCall.check_code).notEquals(Long.ZERO)) {
                return Object.assign({ success: false }, resultBody);
            }
            this.info.voipStatus = respCall.info.status;
            for (const uid of toUsers) {
                if (!this.info.participant.has(uid)) { // 添加成员, 默认设置成 calling
                    const p = new RtcParticipant(uid);
                    p.status = im_proto.VoipStatus.CALLING;
                    this.info.participant.set(uid, p);
                }
            }
            // 自己设置成已经进入通话
            const self = new RtcParticipant(this.info.callerId);
            self.status = im_proto.VoipStatus.ONTHECALL;
            this.info.participant.set(this.info.callerId, self);
            yield this.joinChannel(this.info.token, this.info.channelId, this.info.userId);
            this.startCallingTimer(callParams);
            this.emit(RtcEvent.StartCall, this.info);
            return Object.assign({ success: true }, resultBody);
        });
    }
    // cmd
    get isBusy() {
        return !!this.info && [im_proto.VoipStatus.CALLING, im_proto.VoipStatus.RINGING, im_proto.VoipStatus.ONTHECALL].includes(this.info.voipStatus);
    }
    isCurrentChannel(m) {
        return (!!this.info) && (this.info.channelId === m.channelId || this.info.channelId === m.refChannelId);
    }
    // 音频 <--> 音视频 切换
    checkChangeChatType(userId, type) {
        this.checkInfo();
        const oldType = this.info.voipType;
        if (oldType !== type) {
            this.info.voipType = type;
            if (type === im_proto.VoipType.VOIP_TYPE_AUDIOONLY) {
                this.toggleVideo(false);
            }
            // this.getService(ServiceIdentity.Monitor).emitTracker(TrackerItem.RtcSwitch, {
            //     imsdk_result: '1',
            //     switch_type: type === im_proto.VoipType.VOIP_TYPE_AUDIOONLY ? '2' : '1'
            // });
            this.emit(RtcEvent.ChatTypeChanged, {
                changeUid: userId,
                info: this.info,
                oldType
            });
            return true;
        }
        return false;
    }
    acceptedToOnTheCall() {
        return __awaiter(this, void 0, void 0, function* () {
            this.checkInfo();
            if (this.info.voipStatus === im_proto.VoipStatus.ACCEPTED) {
                this.startOnTheCallTimer();
                this.stopCallingTimer();
                yield this.updateStatus(im_proto.VoipStatus.ONTHECALL);
            }
        });
    }
    callingToOnTheCall() {
        return __awaiter(this, void 0, void 0, function* () {
            this.checkInfo();
            if (this.info.voipStatus === im_proto.VoipStatus.CALLING) {
                this.startOnTheCallTimer();
                this.stopCallingTimer();
                yield this.updateStatus(im_proto.VoipStatus.ONTHECALL);
            }
        });
    }
    handleRtcCmdMessage(m) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        return __awaiter(this, void 0, void 0, function* () {
            this.checkInit();
            const current = (_a = this.info) === null || _a === void 0 ? void 0 : _a.voipStatus;
            Logger.ctxDebug(this.ctx, 'receive rtc cmd msg:', m, `current state: ${im_proto.VoipStatus[current ? current : im_proto.VoipStatus.UNAVAILABLE]}`);
            // 多端同步, 收到其他设备同uid的消息
            if (m.userId === this.ctx.option.userId) {
                if (m.deviceId && this.ctx.option.deviceId !== m.deviceId) {
                    if (this.info !== null && current === im_proto.VoipStatus.RINGING && this.isCurrentChannel(m)) {
                        switch (m.voipStatus) {
                            case im_proto.VoipStatus.OCCUPIED:
                                this.emit(RtcEvent.SelfOtherDeviceOccupied, m);
                                this.info.voipStatus = im_proto.VoipStatus.IDLE;
                                break;
                            case im_proto.VoipStatus.ACCEPTED:
                                this.emit(RtcEvent.SelfOtherDeviceAccpet, m);
                                this.info.voipStatus = im_proto.VoipStatus.IDLE;
                                break;
                            case im_proto.VoipStatus.REFUSED:
                                this.emit(RtcEvent.SelfOtherDeviceDeny, m);
                                this.info.voipStatus = im_proto.VoipStatus.REFUSED;
                                break;
                        }
                    }
                }
                // 不处理其他给自己的消息
                return true;
            }
            // 收到其他用户的rtc cmd消息
            if (m.voipStatus === im_proto.VoipStatus.CALLING) {
                if ((current === im_proto.VoipStatus.RINGING || current === im_proto.VoipStatus.ONTHECALL) && this.isCurrentChannel(m)) {
                    // 多人处理
                    this.checkChangeChatType(m.userId, m.voipType);
                }
                else { // 是新呼入的
                    const channel = m.refChannelId && m.refChannelId !== '' ? m.refChannelId : m.channelId;
                    let result;
                    // 检查下当前状态, 规避ringing同时deny的时序问题
                    if (((_b = this.info) === null || _b === void 0 ? void 0 : _b.voipStatus) !== im_proto.VoipStatus.REFUSED) {
                        result = yield this.api.UpdateVoip({
                            channelId: channel,
                            status: im_proto.VoipStatus.RINGING,
                            type: m.voipType
                        });
                        this.resolve(ServiceIdentity.Monitor).emitTracker(TrackerItem.RtcReceiveRing, {
                            imsdk_result: result.status_code === im_proto.VoipStatusCode.SUCCESS ? '1' : '0',
                            error_code: (_c = result.status_code) === null || _c === void 0 ? void 0 : _c.toString(),
                            conversation_id: (_f = (_e = (_d = result === null || result === void 0 ? void 0 : result.info) === null || _d === void 0 ? void 0 : _d.con_short_id) === null || _e === void 0 ? void 0 : _e.toString()) !== null && _f !== void 0 ? _f : '',
                            is_video: ((_g = result === null || result === void 0 ? void 0 : result.info) === null || _g === void 0 ? void 0 : _g.v_type) === 2 ? 1 : 0,
                            logid: (_h = result === null || result === void 0 ? void 0 : result.info) === null || _h === void 0 ? void 0 : _h.channel_id
                        });
                        if (result.status_code !== im_proto.VoipStatusCode.SUCCESS) {
                            this.emit(RtcEvent.RingFail, {
                                message: m
                            });
                            throw new BaseError({
                                ctx: this.ctx,
                                type: ErrorType.RtcInvalidParam,
                                sender: this,
                                msg: `update voip status error: ${im_proto.VoipStatusCode[result.status_code]}`
                            });
                        }
                    }
                    if (this.isBusy) {
                        if (this.isCurrentChannel(m)) {
                            this.checkChangeChatType(m.userId, m.voipType);
                        }
                        else {
                            this.emit(RtcEvent.SelfOccupied, m);
                            const r = yield this.api.UpdateVoip({
                                channelId: channel,
                                status: im_proto.VoipStatus.OCCUPIED,
                                type: m.type
                            });
                            if ((r === null || r === void 0 ? void 0 : r.status_code) !== im_proto.VoipStatusCode.SUCCESS) {
                                throw new BaseError({
                                    ctx: this.ctx,
                                    type: ErrorType.RtcInvalidParam,
                                    sender: this,
                                    msg: `update voip status error: ${im_proto.VoipStatusCode[r.status_code]}`
                                });
                            }
                        }
                    }
                    else {
                        this.info = RtcChatInfo.fromVoipInfo(result.info);
                        this.emit(RtcEvent.SelfRinging, this.info);
                    }
                }
            }
            else if (this.isCurrentChannel(m)) {
                // 是当前的channel 则处理下命令消息
                let typeChanged = false;
                if (this.info !== null && this.info.channelId === m.channelId) {
                    if (this.isBusy) {
                        typeChanged = this.checkChangeChatType(m.userId, m.voipType);
                    }
                }
                switch (m.voipStatus) {
                    // 对方取消呼叫
                    case im_proto.VoipStatus.CANCELLED:
                    case im_proto.VoipStatus.UNAVAILABLE:
                        if (current === im_proto.VoipStatus.RINGING) {
                            yield this.updateStatus(im_proto.VoipStatus.TERMINATED);
                            this.emit(RtcEvent.CallCanceled, m);
                            yield this.reset(this.api);
                        }
                        break;
                    // 对方拒绝
                    case im_proto.VoipStatus.REFUSED:
                    case im_proto.VoipStatus.OCCUPIED:
                        // 先删掉participant
                        (_j = this.info) === null || _j === void 0 ? void 0 : _j.participant.delete(m.userId);
                        if (m.voipStatus === im_proto.VoipStatus.REFUSED) {
                            this.emit(RtcEvent.CalleeDeny, m);
                        }
                        else if (m.voipStatus === im_proto.VoipStatus.OCCUPIED) {
                            this.emit(RtcEvent.CalleeOccupied, m);
                        }
                        if (current === im_proto.VoipStatus.CALLING) {
                            // 起始发起呼叫
                            // 当所有被叫人都拒绝时, participant 只会有自己, 所以可以reset
                            if (this.info.participant.size === 1) { // 只有两方通话时, 拒绝通话或者占线才能reset
                                yield this.reset(this.api);
                            }
                        }
                        else if (((_k = this.info) === null || _k === void 0 ? void 0 : _k.voipStatus) === im_proto.VoipStatus.ONTHECALL) { // 不在呼叫时才能执行操作, 否则只更新下状态
                            // 中途发起呼叫
                            // 删掉拒绝的用户
                            this.info.participant.delete(m.userId);
                            // 计算剩余需要call的用户
                            const callingParticipant = [];
                            for (const par of this.info.participant.values()) {
                                if (par && par.status === im_proto.VoipStatus.CALLING) {
                                    callingParticipant.push(par.userId);
                                }
                            }
                            if (callingParticipant.length === 0) {
                                this.stopCallingTimer();
                            }
                            else {
                                // 更新被呼叫人, 但是不重置计时器
                                this.callParams.calleeIds = callingParticipant;
                            }
                        }
                        break;
                    // 对方接受
                    case im_proto.VoipStatus.ACCEPTED:
                        if (current === im_proto.VoipStatus.CALLING) {
                            yield this.callingToOnTheCall();
                        }
                        else if (current === im_proto.VoipStatus.ONTHECALL) {
                            // 正在通话中需要计算是否还需要继续call
                            const callee = this.info.participant.get(m.userId);
                            if (callee) {
                                callee.status = im_proto.VoipStatus.ONTHECALL; // 直接刷成 OnTheCall
                            }
                            const callingParticipant = [];
                            for (const par of this.info.participant.values()) {
                                if (par && par.status === im_proto.VoipStatus.CALLING) {
                                    callingParticipant.push(par.userId);
                                }
                            }
                            if (callingParticipant.length === 0) {
                                this.stopCallingTimer();
                            }
                            else {
                                // 更新被呼叫人, 但是不重置计时器
                                this.callParams.calleeIds = callingParticipant;
                            }
                        }
                        this.emit(RtcEvent.CalleeAccept, {
                            message: m,
                            typeChanged
                        });
                        break;
                    // 对方正在被呼叫, 并且成功收到了
                    case im_proto.VoipStatus.RINGING:
                        if (current === im_proto.VoipStatus.CALLING) {
                            this.emit(RtcEvent.CalleeRinging, m);
                        }
                        else if (current === im_proto.VoipStatus.CANCELLED) {
                            yield this.updateStatus(im_proto.VoipStatus.CANCELLED); // FIXME: 这里实现和 Android 不一样
                        }
                        break;
                }
            }
            return true;
        });
    }
    startOnTheCallTimer() {
        var _a;
        (_a = this.onTheCallTimer) === null || _a === void 0 ? void 0 : _a.stop();
        this.onTheCallTimer = new Ticker(this.ctx, RtcManager.hbInterval);
        // 每10s发心跳
        this.onTheCallTimer.onTick.subscribe(() => __awaiter(this, void 0, void 0, function* () {
            yield this.api.ChannelHeartbeat({
                channelId: this.info.channelId
            });
        }));
        this.checkInfo();
        this.onTheCallTimer.restart();
    }
    stopOnTheCallTimer() {
        if (!this.onTheCallTimer) {
            return;
        }
        this.onTheCallTimer.stop();
        if (this.info) {
            this.info.chatDuration = this.onTheCallTimer.duration;
        }
    }
    startCallingTimer(params) {
        var _a;
        this.callParams = params;
        (_a = this.callingTimer) === null || _a === void 0 ? void 0 : _a.stop();
        this.callingTimer = new Ticker(this.ctx, RtcManager.callingIntervalTimeout, RtcManager.callTimeout);
        // 每5s尝试一次 callVoip
        this.callingTimer.onTick.subscribe(() => __awaiter(this, void 0, void 0, function* () {
            if (!this.info) {
                this.callingTimer.stop();
                return;
            }
            Logger.ctxDebug(this.ctx, 'call timer invoke');
            yield this.api.CallVoip(this.callParams);
        }));
        // 60s 超时后结束通话
        this.callingTimer.onDone.subscribe(() => __awaiter(this, void 0, void 0, function* () {
            var _b;
            Logger.ctxDebug(this.ctx, 'call timeout');
            // 判断是不是中途呼叫, 如果不是中途呼叫则可以释放资源
            if (((_b = this.info) === null || _b === void 0 ? void 0 : _b.voipStatus) !== im_proto.VoipStatus.ONTHECALL) {
                yield this.reset(this.api);
            }
            this.emit(RtcEvent.CallTimeout, this.info);
        }));
        this.callingTimer.onStop.subscribe(() => {
            Logger.ctxDebug(this.ctx, 'call timer stop');
        });
        this.callingTimer.start();
        Logger.ctxDebug(this.ctx, 'call timer start');
    }
    stopCallingTimer() {
        var _a;
        (_a = this.callingTimer) === null || _a === void 0 ? void 0 : _a.stop();
    }
    // #endregion
    // #region sdk methods
    getLocalVideoStats() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.client.getLocalVideoStats();
        });
    }
    getLocalAudioStats() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.client.getLocalAudioStats();
        });
    }
    getRemoteVideoStats() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.client.getRemoteVideoStats();
        });
    }
    getRemoteAudioStats() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.client.getRemoteAudioStats();
        });
    }
}
RtcManager.hbInterval = 10 * 1000; // ms
RtcManager.callTimeout = 60 * 1000; // ms
RtcManager.callingIntervalTimeout = 5 * 1000;

export { RtcManager };
//# sourceMappingURL=RtcManager.js.map
